import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./PublicAssessmentSubmitPage.css";
import { ASSESSMENT_IDENTITY_KEY, ASSESSMENT_SUBMISSION_KEY, readAssessmentStorage } from "./assessmentStorage";

export { ASSESSMENT_IDENTITY_KEY, ASSESSMENT_SUBMISSION_KEY } from "./assessmentStorage";
export const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];
const ACCEPTED_EXTENSIONS = [".pdf", ".jpg", ".png", ".webp"];
const TERMINAL_STATES = new Set(["needs_review", "failed", "grading_failed", "evaluated"]);
const POLL_INTERVAL_MS = 2500;

const FILE_SLOTS = [
  { key: "completed", field: "completed_question_paper", label: "Completed question paper", required: true },
  { key: "answerKey", field: "answer_key", label: "Answer key", required: true },
  { key: "clean", field: "empty_question_paper", label: "Clean question paper", required: false },
];

export function validateAssessmentFile(file) {
  if (!file) return "Please select a file.";
  if (!file.name) return "The selected file must have a filename.";
  if (file.size === 0) return "The selected file is empty.";
  const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (!ACCEPTED_FILE_TYPES.includes(file.type) && !ACCEPTED_EXTENSIONS.includes(extension)) {
    return "Use a PDF, JPG, PNG or WebP file.";
  }
  return "";
}

export function getIngestionMessage(state = {}) {
  const messages = {
    created: "Creating your submission…",
    uploading: "Storing the uploaded files…",
    queued: "Your paper is queued for processing…",
    retry_ingestion: "Processing is being retried…",
    processing_files: "Preparing the uploaded files…",
    extracting_questions: "Reading the questions…",
    extracting_student_answers: "Reading the student's answers…",
    extracting_answer_key: "Reading the answer key…",
    matching: "Matching answers to questions…",
    storing_results: "Saving the processed paper…",
    grading_queued: "Grading has been queued…",
    loading_canonical_artifacts: "Preparing the processed paper for grading…",
    evaluating_questions: "Evaluating the answers…",
  };
  if (state.status === "ready_for_grading") return "Your paper was uploaded and processed successfully. Starting grading…";
  if (state.status === "grading_failed") return state.message || "Grading could not be started or completed. You can retry grading.";
  return messages[state.stage] || messages[state.status] || "Processing your submission…";
}

function payloadOf(raw) {
  return raw?.data && typeof raw.data === "object" ? { ...raw, ...raw.data } : raw || {};
}

function safeError(error, fallback) {
  const status = error?.response?.status;
  const data = payloadOf(error?.response?.data);
  if (data.message) return data.message;
  if (!error?.response) return "We could not reach the server. Check your connection and try again.";
  if (status === 400) return "Please check the selected files and submission details.";
  if (status === 402) return "There are not enough credits for this assessment.";
  if (status === 403) return "Your email verification has expired. Please verify again.";
  if (status === 409) return "Your saved identity no longer matches this email. Please verify again.";
  if (status === 413) return "The upload was too large for the server. Please use smaller files and try again.";
  if (status >= 500) return "The server could not process the submission. Please try again.";
  return fallback;
}

export default function PublicAssessmentSubmitPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [identityMessage, setIdentityMessage] = useState("");
  const [identityBusy, setIdentityBusy] = useState(false);
  const [files, setFiles] = useState({ completed: null, answerKey: null, clean: null });
  const [fileErrors, setFileErrors] = useState({});
  const [previews, setPreviews] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submissionId, setSubmissionId] = useState("");
  const [submission, setSubmission] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const mountedRef = useRef(true);
  const submittingRef = useRef(false);
  const gradingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const pollSubmissionRef = useRef(null);
  const previewsRef = useRef({});

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  }, []);

  const clearSavedIdentity = useCallback(() => {
    localStorage.removeItem(ASSESSMENT_IDENTITY_KEY);
    localStorage.removeItem(ASSESSMENT_SUBMISSION_KEY);
    stopPolling();
    setIdentity(null);
    setSubmissionId("");
    setSubmission(null);
  }, [stopPolling]);

  const gradeSubmission = useCallback(async (id, verifiedIdentity) => {
    if (gradingRef.current) return;
    gradingRef.current = true;
    stopPolling();
    setStatusMessage("Starting grading…");
    try {
      const response = await axios.post(`/api/assessments/submissions/${encodeURIComponent(id)}/grade`, {
        email: verifiedIdentity.email,
        user_id: verifiedIdentity.user_id,
      });
      if (!mountedRef.current) return;
      const next = payloadOf(response.data);
      setSubmission(next);
      setStatusMessage(getIngestionMessage(next));
      pollTimerRef.current = setTimeout(() => pollSubmissionRef.current?.(id, verifiedIdentity), POLL_INTERVAL_MS);
    } catch (error) {
      if (!mountedRef.current) return;
      const data = payloadOf(error?.response?.data);
      setSubmission((old) => ({ ...(old || {}), ...data, status: data.status || "grading_failed" }));
      setStatusMessage(safeError(error, "Grading could not be started. Please retry."));
      if ([403, 409].includes(error?.response?.status) && data.error !== "invalid_starting_status") clearSavedIdentity();
    } finally {
      gradingRef.current = false;
    }
  }, [clearSavedIdentity, stopPolling]);

  const pollSubmission = useCallback(async (id, verifiedIdentity) => {
    try {
      const response = await axios.get(`/api/assessments/submissions/${encodeURIComponent(id)}`, {
        params: { email: verifiedIdentity.email, user_id: verifiedIdentity.user_id },
      });
      if (!mountedRef.current) return;
      const next = payloadOf(response.data);
      setSubmission(next);
      setStatusMessage(getIngestionMessage(next));
      if (next.status === "ready_for_grading") {
        gradeSubmission(id, verifiedIdentity);
      } else if (next.status === "evaluated") {
        stopPolling();
        localStorage.removeItem(ASSESSMENT_SUBMISSION_KEY);
        navigate(`/assessment-report/${encodeURIComponent(id)}`);
      } else if (TERMINAL_STATES.has(next.status)) {
        stopPolling();
      } else {
        pollTimerRef.current = setTimeout(() => pollSubmission(id, verifiedIdentity), POLL_INTERVAL_MS);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      stopPolling();
      setStatusMessage(safeError(error, "We could not check processing status. Please try again."));
      if ([403, 409].includes(error?.response?.status)) clearSavedIdentity();
    }
  }, [clearSavedIdentity, gradeSubmission, navigate, stopPolling]);

  pollSubmissionRef.current = pollSubmission;

  useEffect(() => {
    mountedRef.current = true;
    const ownedPreviewUrls = previewsRef.current;
    const storedIdentity = readAssessmentStorage(ASSESSMENT_IDENTITY_KEY);
    const storedSubmission = readAssessmentStorage(ASSESSMENT_SUBMISSION_KEY);
    if (storedIdentity?.email && storedIdentity?.user_id) {
      setEmail(storedIdentity.email);
      axios.get("/api/public/oral/email-verified", { params: storedIdentity })
        .then((response) => {
          if (!mountedRef.current) return;
          const verified = payloadOf(response.data).verified;
          if (!verified) { clearSavedIdentity(); setIdentityMessage("Please verify your email again."); return; }
          setIdentity(storedIdentity);
          setIdentityMessage("Email verified.");
          if (storedSubmission?.submission_id) {
            setSubmissionId(storedSubmission.submission_id);
            pollSubmission(storedSubmission.submission_id, storedIdentity);
          }
        })
        .catch(() => { if (mountedRef.current) setIdentityMessage("Could not restore verification. Please verify again."); });
    }
    return () => {
      mountedRef.current = false;
      stopPolling();
      Object.values(ownedPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [clearSavedIdentity, pollSubmission, stopPolling]);

  const sendCode = async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) { setIdentityMessage("Please enter your email address."); return; }
    setIdentityBusy(true);
    try {
      await axios.post("/api/public/oral/send-email-code", { email: normalized });
      setEmail(normalized); setCodeSent(true); setIdentityMessage("Verification code sent.");
    } catch (error) { setIdentityMessage(safeError(error, "Could not send the verification code.")); }
    finally { setIdentityBusy(false); }
  };

  const verifyCode = async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !code.trim()) { setIdentityMessage("Enter your email and verification code."); return; }
    setIdentityBusy(true);
    try {
      const response = await axios.post("/api/public/oral/verify-email-code", {
        email: normalized, code: code.trim(), marketing_email_consent: false,
      });
      const data = payloadOf(response.data);
      if (!data.user_id) throw new Error("missing user id");
      const nextIdentity = { email: (data.user_email || normalized).toLowerCase(), user_id: data.user_id };
      localStorage.setItem(ASSESSMENT_IDENTITY_KEY, JSON.stringify(nextIdentity));
      setEmail(nextIdentity.email); setIdentity(nextIdentity); setIdentityMessage("Email verified.");
    } catch (error) { setIdentityMessage(safeError(error, "Email verification failed. Please request a new code.")); }
    finally { setIdentityBusy(false); }
  };

  const chooseFile = (key, file) => {
    const error = validateAssessmentFile(file);
    setFileErrors((old) => ({ ...old, [key]: error }));
    if (error) return;
    if (previewsRef.current[key]) URL.revokeObjectURL(previewsRef.current[key]);
    const url = URL.createObjectURL(file);
    previewsRef.current[key] = url;
    setPreviews((old) => ({ ...old, [key]: url }));
    setFiles((old) => ({ ...old, [key]: file }));
  };

  const removeFile = (key) => {
    if (previewsRef.current[key]) URL.revokeObjectURL(previewsRef.current[key]);
    delete previewsRef.current[key];
    setPreviews((old) => ({ ...old, [key]: undefined }));
    setFiles((old) => ({ ...old, [key]: null }));
    setFileErrors((old) => ({ ...old, [key]: "" }));
  };

  const submit = async () => {
    if (submittingRef.current || !identity) return;
    const errors = {};
    FILE_SLOTS.forEach(({ key, required }) => { if (required && !files[key]) errors[key] = "This file is required."; });
    setFileErrors(errors);
    if (Object.keys(errors).length) { setStatusMessage("Add both required files before submitting."); return; }
    submittingRef.current = true; setUploading(true); setUploadProgress(0); setStatusMessage("Uploading files…");
    const form = new FormData();
    form.append("email", identity.email);
    form.append("user_id", identity.user_id);
    form.append("assessment_type", "psle_english_paper_2");
    FILE_SLOTS.forEach(({ key, field }) => { if (files[key]) form.append(field, files[key]); });
    try {
      const response = await axios.post("/api/assessments/submissions", form, {
        onUploadProgress: ({ loaded, total }) => {
          if (mountedRef.current && total) setUploadProgress(Math.min(100, Math.round((loaded * 100) / total)));
        },
      });
      const data = payloadOf(response.data);
      if (response.status !== 202 || !data.submission_id) throw new Error("Invalid submission response");
      localStorage.setItem(ASSESSMENT_SUBMISSION_KEY, JSON.stringify({ submission_id: data.submission_id }));
      setSubmissionId(data.submission_id); setSubmission(data); setUploadProgress(100);
      setStatusMessage(getIngestionMessage(data));
      pollSubmission(data.submission_id, identity);
    } catch (error) {
      const data = payloadOf(error?.response?.data);
      if (data.submission_id) {
        setSubmissionId(data.submission_id);
        setSubmission({ ...data, status: data.status || "failed" });
        localStorage.setItem(ASSESSMENT_SUBMISSION_KEY, JSON.stringify({ submission_id: data.submission_id }));
      }
      setStatusMessage(safeError(error, "The submission could not be created. Please try again."));
      if ([403, 409].includes(error?.response?.status)) clearSavedIdentity();
    } finally { submittingRef.current = false; if (mountedRef.current) setUploading(false); }
  };

  const startNew = () => {
    stopPolling(); localStorage.removeItem(ASSESSMENT_SUBMISSION_KEY);
    setSubmissionId(""); setSubmission(null); setStatusMessage(""); setUploadProgress(0);
  };

  return <div className="assessment-submit-page">
    <header><p className="eyebrow">PSLE English · Paper 2</p><h1>Submit an assessment</h1><p>Upload the completed paper and its answer key. We’ll prepare them for grading.</p></header>
    {!identity ? <section className="assessment-card"><h2>Verify your email</h2>
      <label>Email<input aria-label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={identityBusy} /></label>
      <button type="button" onClick={sendCode} disabled={identityBusy}>Send verification code</button>
      {codeSent && <><label>Verification code<input aria-label="Verification code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} /></label><button type="button" onClick={verifyCode} disabled={identityBusy}>Verify email</button></>}
      {identityMessage && <p role="status">{identityMessage}</p>}
    </section> : <>
      <section className="assessment-card identity-row"><span>Verified as <strong>{identity.email}</strong></span><button className="link-button" type="button" onClick={clearSavedIdentity}>Use another email</button></section>
      {!submissionId && <section className="assessment-card"><h2>Assessment files</h2><p className="file-help">Accepted: PDF, JPG, PNG and WebP. The clean paper is optional.</p>
        <div className="file-grid">{FILE_SLOTS.map((slot) => <FileSlot key={slot.key} slot={slot} file={files[slot.key]} preview={previews[slot.key]} error={fileErrors[slot.key]} onChoose={(file) => chooseFile(slot.key, file)} onRemove={() => removeFile(slot.key)} />)}</div>
        <button className="primary-button" type="button" onClick={submit} disabled={uploading}>{uploading ? "Uploading…" : "Upload and process"}</button>
        {(uploading || uploadProgress > 0) && <div className="upload-progress"><progress aria-label="Upload progress" max="100" value={uploadProgress} /><span>{uploadProgress}% uploaded</span></div>}
      </section>}
      {(statusMessage || submissionId) && <section className="assessment-card processing" aria-live="polite"><h2>Submission status</h2>{submissionId && <p className="submission-id">Submission ID: {submissionId}</p>}<p role="status">{statusMessage}</p>
        {submission?.status === "needs_review" && <ReviewDetails submission={submission} />}
        {submission?.status === "grading_failed" && <button type="button" onClick={() => gradeSubmission(submissionId, identity)} disabled={gradingRef.current}>Retry grading</button>}
        {submissionId && submission && !TERMINAL_STATES.has(submission.status) && !pollTimerRef.current && <button type="button" onClick={() => pollSubmission(submissionId, identity)}>Check status again</button>}
        {submission?.status === "failed" && <>{submission.error_code && <p>Error: {submission.error_code}</p>}<button type="button" onClick={startNew}>Start a new submission</button></>}
      </section>}
    </>}
  </div>;
}

function FileSlot({ slot, file, preview, error, onChoose, onRemove }) {
  const inputRef = useRef(null);
  const receive = (event) => { event.preventDefault(); const selected = event.dataTransfer?.files?.[0]; if (selected) onChoose(selected); };
  return <div className="file-slot"><h3>{slot.label}{slot.required ? " *" : " (optional)"}</h3>
    <div className="drop-zone" onDragOver={(e) => e.preventDefault()} onDrop={receive}>
      <input ref={inputRef} id={`file-${slot.key}`} className="sr-only" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => e.target.files[0] && onChoose(e.target.files[0])} />
      <button type="button" onClick={() => inputRef.current?.click()}>{file ? "Replace file" : "Choose file"}</button><p>or drag and drop here</p>
    </div>
    {error && <p className="error" role="alert">{error}</p>}
    {file && <div className="file-preview"><p title={file.name}>{file.name}</p>{file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf") ? <object data={preview} type="application/pdf" aria-label={`${slot.label} preview`} /> : <img src={preview} alt={`${slot.label} preview`} />}<button type="button" onClick={onRemove}>Remove {slot.label.toLowerCase()}</button></div>}
  </div>;
}

function ReviewDetails({ submission }) {
  return <div className="review-details"><h3>Manual review needed</h3>{submission.message && <p>{submission.message}</p>}
    {!!submission.review_reasons?.length && <><h4>Review reasons</h4><ul>{submission.review_reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></>}
    {submission.matching_summary && <><h4>Matching summary</h4><pre>{JSON.stringify(submission.matching_summary, null, 2)}</pre></>}
  </div>;
}
